import type { SeedAppConfig } from '@/types'

/**
 * Human label for a seed app key, matching what the CONTAINER shows in its app bar.
 *
 * The container is the reference implementation (`apps/container/portal/src/components/AppBar.tsx`,
 * `shortName`): it reads the name the seed's manifest declares in `appsConfig` and strips
 * the redundant `"<seed display name> "` prefix, so `"Rubberway V2 Tire Manufacturer"`
 * renders as `"Tire Manufacturer"`.
 *
 * The console used to derive labels from the KEY instead — three different ways in three
 * places (`portal-brand` → `"brand"`, → `"portal-brand"`, and the full unstripped
 * `"Rubberway V2 Tire Manufacturer"`) — so the same app read differently depending on
 * which screen you were on, and none of them matched the container. Route every console
 * app label through here.
 *
 * Naming varies per seed and prefixing is NOT universal: most seeds prefix
 * (`"CAP Tracker Factory"` → `"Factory"`), but some declare bare names
 * (`tg-coco-fast`: `"Foundation Portal"`). The strip is a no-op for those, which is
 * exactly what the container does — the full name is already the short one.
 */
export function appLabel(
  key: string,
  appsConfig?: Record<string, SeedAppConfig>,
  seedDisplayName?: string,
): string {
  const declared = appsConfig?.[key]?.name
  if (!declared) return prettifyKey(key)
  const prefix = `${seedDisplayName ?? ''} `
  return seedDisplayName && declared.startsWith(prefix)
    ? declared.slice(prefix.length)
    : declared
}

/**
 * Fallback when the served version declares no `appsConfig` entry for this key — only
 * reachable for a version whose metadata predates `appsConfig` (no seed in production is
 * in that state). Turns `portal-brand` into `Brand` rather than leaking the raw key.
 */
function prettifyKey(key: string): string {
  if (key === 'pwa') return 'PWA'
  const base = key.replace(/^portal-?/, '') || 'portal'
  return base
    .split('-')
    .map(w => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ')
}
